import * as React from 'react';
import * as autoBind from 'react-autobind';
import * as moment from 'moment';
import {TscCheckboxAlignment, ControlButton, TscButtonType, InlineLabeledCheckbox, TextInput} from '@techsmith/tsc-cloud-style-guide';
import CancellationToken from '../../utils/CancellationToken';
import WebConnector from '../../services/WebConnector/WebConnector';
import MediaPreview from '../MediaPreview/MediaPreview';
import UploadProgressBar from '../UploadProgressBar/UploadProgressBar';
import Constants, {ModalStates} from '../../globals/Constants';
import ContentTypeParser from '../../utils/ContentTypeParser/ContentTypeParser';
import Actions from '../../redux/Actions';
import Store from '../../redux/Store';
import Strings from '../../globals/Strings';
import DeadlineInput from '../DeadlineInput/DeadlineInput';
import ReviewService from '../../services/ReviewService/ReviewService';
import UserMessaging from '../../utils/UserMessaging';
import Themeable from '../../types/Themeable';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import ReviewModel from '../../types/ReviewModel';
import ReviewRouterService from '../../services/ReviewRouterService/ReviewRouterService';
import AddNewVersionModel from '../../types/AddNewVersionModel';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';

export default class ReviewDetailsModal extends React.Component<ReviewDetailsModalProps, ReviewDetailsModalState> {
   readonly state: ReviewDetailsModalState = {
      title: this.props.title,
      prompt: this.props.prompt,
      uploadProgress: 0.0,
      uploadSource: this.props.uploadSource,
      uploadUrl: this.props.uploadUrl,
      uploadType: ContentTypeParser.getContentTypeIsVideo(this.props.file.type) ? Strings.labels.startReviewModal.reviewDetails.mediaTypes.video : Strings.labels.startReviewModal.reviewDetails.mediaTypes.image,
      reviewByDate: this.props.reviewByDate,
      isSubmitting: false,
      notifyReviewersOfVersion: true
   };

   cancellationToken: CancellationToken;

   constructor(props: ReviewDetailsModalProps) {
      super(props);
      autoBind(this);

      this.cancellationToken = new CancellationToken();

      if (!this.props.uploaded) {
         const onUploadProgress = (progress: number): void => this.setState({uploadProgress: progress});

         WebConnector.upload(props.file, this.cancellationToken, onUploadProgress).then(fileId => {
            this.setState({uploadSource: fileId, uploadProgress: 1.0});

            return WebConnector.getFileUrl(fileId).then((url: string) => {
               return WebConnector.getFileContentType(fileId).then((mediaContentType: string) => {
                  if (this.props.review) {
                     GoogleAnalyticsService.trackVersioning(Store.getState().currentUserId, Constants.googleAnalytics.versioning.actions.VersionMediaUploaded, mediaContentType);
                  } else {
                     GoogleAnalyticsService.trackCreateReview(Store.getState().currentUserId, Constants.googleAnalytics.createReview.actions.uploadMedia, mediaContentType);
                  }

                  if (this.props.modalState === ModalStates.newReview) {
                     Store.dispatch(Actions.resetReview());
                  }

                  if (!ContentTypeParser.getContentTypeIsVideo(mediaContentType)) {
                     const reader = new FileReader();

                     reader.onloadend = (e: any) => {
                        this.props.onUploadComplete(fileId, url).then(() => this.setState({uploadUrl: url}));
                     };

                     reader.readAsArrayBuffer(props.file.slice(0, Constants.imageLayout.maxFileBytesToRead));
                  } else {
                     this.props.onUploadComplete(fileId, url).then(() => this.setState({uploadUrl: url}));
                  }
               });
            });
         }).catch(() => {
            !this.cancellationToken.isCancellationRequested && UserMessaging.warn(Strings.errors.failedUploadMedia);
            this.onClickBack();
         });
      }
   }

   onToggleNotificationOfNewVersion(): void {
      this.setState({notifyReviewersOfVersion: !this.state.notifyReviewersOfVersion});
   }

   onTitleChange(event: any): void {
      this.setState({title: event.target.value});
   }

   onPromptChange(event: any): void {
      this.setState({prompt: event.target.value});
   }

   async onCreateReview() {
      const {title, prompt, reviewByDate, uploadSource, isSubmitting, uploadType} = this.state;

      if (isSubmitting) {
         return;
      }

      this.setState({isSubmitting: true});

      try {
         await ReviewService.postReview({Title: title.trim(), Description: prompt.trim(), ReviewByDate: reviewByDate, MediaSource: uploadSource, ReviewerEmailAddresses: []});
         this.setState({isSubmitting: false});
         GoogleAnalyticsService.trackCreateReview(Store.getState().currentUserId, Constants.googleAnalytics.createReview.actions.reviewCreated, uploadType);
         this.props.onNextClick();
      } catch {
         this.setState({isSubmitting: false});
         UserMessaging.warn(Strings.errors.failedAddingReview);
      }
   }

   async onCreateVersion(): Promise<void> {
      const {review, history, onNextClick} = this.props;
      const {prompt, reviewByDate, uploadSource, isSubmitting, notifyReviewersOfVersion, uploadType} = this.state;

      if (isSubmitting) {
         return;
      }

      this.setState({isSubmitting: true});

      try {
         const versionInfo: AddNewVersionModel = {
            MediaSource: uploadSource,
            Description: prompt.trim(),
            ReviewByDate: reviewByDate,
            NotifyExistingReviewers: notifyReviewersOfVersion && review.Reviewers.some(reviewer => reviewer.IsUserDeleted === false && reviewer.Role === Constants.reviewerRoles.reviewer && reviewer.WasUserRoleRemoved === false)
         };

         const version = await ReviewService.postVersion(review.Id, versionInfo);
         await ReviewService.loadReview(review.Id, version.VersionNumber);
         GoogleAnalyticsService.trackVersioning(Store.getState().currentUserId, Constants.googleAnalytics.versioning.actions.versionCreated.replace('{param0}', version.VersionNumber.toString()), uploadType);
         history.push(ReviewRouterService.createCanonicalReviewUrl(review.Id, version.VersionNumber));
         this.setState({isSubmitting: false});
         onNextClick();
      } catch {
         this.setState({isSubmitting: false});
         UserMessaging.warn(Strings.errors.failedAddingReview);
      }
   }

   onClickBack(): void {
      this.cancellationToken.cancel();
      this.setState({
         uploadProgress: 0.0,
         uploadSource: '',
         uploadUrl: ''
      });

      this.props.onBackClick(this.state.title, this.state.prompt, this.state.reviewByDate);
   }

   changeDate(date: Date): void {
      this.setState({reviewByDate: date});
   }

   removeDate(): void {
      this.setState({reviewByDate: null});
   }

   componentWillUnmount(): void {
      this.cancellationToken.cancel();
   }

   isValidReviewByDate(): boolean {
      const d = moment(this.state.reviewByDate);
      return !this.state.reviewByDate || (d != null && d.isValid());
   }

   getTitleError(): string {
      const {title} = this.state;

      if (!title.trim()) {
         return Strings.errors.missingTitle;
      }

      if (title.length > Constants.title.maxLength) {
         return Strings.errors.invalidTitleLength.replace('{param0}', Constants.title.maxLength + '');
      }
   }

   getPrimaryButtonText(reviewerCount: number): string {
      const {modalState} = this.props;

      if (modalState === ModalStates.newReview) {
         return Strings.labels.createReview;
      } else if (modalState === ModalStates.newVersion && reviewerCount === 0 || !this.state.notifyReviewersOfVersion) {
         return Strings.labels.versions.createVersion;
      } else {
         return Strings.labels.versions.createVersionWithReviewers;
      }
   }

   render() {
      const {themeName, visible, modalState, review, uploaded} = this.props;
      const {title, prompt, reviewByDate, uploadUrl, uploadType, uploadProgress, isSubmitting, notifyReviewersOfVersion} = this.state;
      const titleErrorText = this.getTitleError();
      const promptErrorText = prompt.length > Constants.prompt.maxLength ? Strings.errors.invalidPromptLength.replace('{param0}', Constants.prompt.maxLength + '') : '';
      const prevButtonDisabled = isSubmitting;
      const nextButtonDisabled = !!titleErrorText || !!promptErrorText || isSubmitting || !title || !this.props.uploaded || !this.isValidReviewByDate();
      const primaryButtonHandler = modalState === ModalStates.newReview ? this.onCreateReview : this.onCreateVersion;
      const reviewerCount = review ? review.Reviewers.filter(reviewer => reviewer.IsUserDeleted === false && reviewer.Role === Constants.reviewerRoles.reviewer && reviewer.WasUserRoleRemoved === false).length : 0;
      const primaryButtonText = this.getPrimaryButtonText(reviewerCount);

      return (
         <div>
            <div className="review-modal-media">
               {visible && uploadUrl && <MediaPreview mediaContentType={uploadType} mediaUrl={uploadUrl} />}
               {!uploaded && <UploadProgressBar progress={uploadProgress} />}
            </div>
            <div className="review-modal-body">
               <div className="modal-message">
                  {isSubmitting && <LoadingSpinner />}
                  {modalState === ModalStates.newReview && <label htmlFor="review-details-title-input" className="input-label">{Strings.labels.startReviewModal.reviewDetails.title.title}</label>}
                  {modalState === ModalStates.newReview && <TextInput sharedId="review-details-title-input" themeName={themeName} placeholder={Strings.labels.startReviewModal.reviewDetails.title.inputPlaceHolder} errorText={titleErrorText} value={title} onChange={this.onTitleChange} disabled={isSubmitting} required={true} testId="review-details-title-input" />}
                  <label htmlFor="review-details-deadline-input" className="input-label">{Strings.labels.startReviewModal.reviewDetails.deadline.title}</label>
                  <DeadlineInput themeName={themeName} reviewByDate={reviewByDate} changeDate={this.changeDate} removeDate={this.removeDate} disabled={isSubmitting} />
                  <label htmlFor="review-details-prompt-input" className="input-label">{Strings.labels.startReviewModal.reviewDetails.description.title}</label>
                  <TextInput sharedId="review-details-prompt-input" rows={3} themeName={themeName} placeholder={Strings.labels.startReviewModal.reviewDetails.description.inputPlaceholder.replace('{param0}', uploadType)} errorText={promptErrorText} onChange={this.onPromptChange} value={prompt} disabled={isSubmitting} testId="review-details-prompt-input" />
               </div>
               {modalState === ModalStates.newVersion && reviewerCount === 1 && <div className="version-message">
                  <InlineLabeledCheckbox labelText={Strings.labels.versions.versionModalMessageSingular} checkboxAlignment={TscCheckboxAlignment.left} isCentered={true} isChecked={notifyReviewersOfVersion} onClick={this.onToggleNotificationOfNewVersion} />
               </div>}
               {modalState === ModalStates.newVersion && reviewerCount > 1 && <div className="version-message">
                  <InlineLabeledCheckbox labelText={Strings.labels.versions.versionModalMessagePlural.replace('{param0}', reviewerCount.toString())} checkboxAlignment={TscCheckboxAlignment.left} isCentered={true} isChecked={notifyReviewersOfVersion} onClick={this.onToggleNotificationOfNewVersion} />
               </div>}
               <div className="button-group">
                  <ControlButton
                     disabled={prevButtonDisabled}
                     buttonType={TscButtonType.secondary}
                     themeName={themeName}
                     isFullWidth={true}
                     onClick={this.onClickBack}
                     label={Strings.labels.back}
                     testId="review-details-modal-delete-button"
                  />
                  <ControlButton
                     disabled={nextButtonDisabled}
                     themeName={themeName}
                     buttonType={TscButtonType.primary}
                     isFullWidth={true}
                     onClick={primaryButtonHandler}
                     label={primaryButtonText}
                     testId="review-details-modal-next-button"
                  />
               </div>
            </div>
         </div>
      );
   }
}

interface ReviewDetailsModalProps extends Themeable {
   file: File;
   uploaded: boolean;
   title: string;
   prompt: string;
   uploadSource: string;
   uploadUrl: string;
   reviewByDate: Date;
   visible: boolean;
   modalState: ModalStates;
   review: ReviewModel;
   history: any;
   onNextClick(): void;
   onUploadComplete(fileId: string, url: string): Promise<void>;
   onBackClick(title: string, prompt: string, reviewByDate: Date): void;
}

interface ReviewDetailsModalState {
   title: string;
   uploadProgress: number;
   prompt: string;
   uploadSource: string;
   uploadUrl: string;
   uploadType: string;
   reviewByDate: Date;
   isSubmitting: boolean;
   notifyReviewersOfVersion: boolean;
}
